/*
 * ANALYTICS-TABLES01 (ТЗ v4 §24–26, FE-FIX21/22/23).
 *
 * Единый query-контракт для трёх таблиц Executive Summary: «Камеры и простои»
 * (control_*), «Сравнение объектов и локаций» (comparison_*) и «Рейтинг смен»
 * (shift_*). Все три живут в одном запросе GET /analytics/executive-summary,
 * поэтому у каждой свой префикс параметров — и один и тот же набор операций:
 * checkbox-фильтры по Объекту/Локации/Камере (+ enum-уровень), серверная
 * сортировка и серверная пагинация.
 *
 * Инварианты:
 *  - total, порядок строк и метрики — от backend. Фронт не сортирует страницу
 *    так, будто это весь набор, и ничего не пересчитывает.
 *  - смена фильтра или сортировки обнуляет offset: иначе пользователь
 *    оказывается на пятой странице набора, в котором страниц две.
 *  - пустой уровень фильтра = «все доступные», в запрос он не уходит.
 *  - имя URL-параметра совпадает с именем параметра backend — адрес читается
 *    как сам запрос, и локальные фильтры таблиц не пересекаются с глобальным
 *    scope страницы (camera_ids vs control_camera_ids).
 */

const HIERARCHY_LEVELS = [
  {
    key: 'objectIds', param: 'object_ids', label: 'Объекты', all: 'Все объекты', optionsKey: 'objects',
  },
  {
    key: 'locationIds', param: 'location_ids', label: 'Локации', all: 'Все локации', optionsKey: 'locations',
  },
  {
    key: 'cameraIds', param: 'camera_ids', label: 'Камеры', all: 'Все камеры', optionsKey: 'cameras',
  },
];

export const CONTROL_STATUS_OPTIONS = [
  { id: 'ONLINE', label: 'Онлайн' },
  { id: 'OFFLINE', label: 'Оффлайн' },
  { id: 'STOPPED', label: 'Остановлена вручную' },
];

export const SHIFT_KEY_OPTIONS = [
  { id: 'shift1', label: 'Смена 1' },
  { id: 'shift2', label: 'Смена 2' },
  { id: 'shift3', label: 'Смена 3' },
];

export const TABLE_LIMIT_OPTIONS = [10, 25, 50, 100];

const definition = ({ id, prefix, wirePrefix, sortBy, sortOrder, sortable, enumLevel }) => ({
  id,
  prefix,
  // Префикс параметров запроса, если он отличается от префикса в URL страницы.
  wirePrefix: wirePrefix || prefix,
  levels: HIERARCHY_LEVELS,
  enumLevel: enumLevel || null,
  defaults: { sortBy, sortOrder, limit: 10, offset: 0 },
  sortable,
});

/** control_*: строки камер с backend-метриками простоя (ТЗ §24). */
export const CONTROL_TABLE = definition({
  id: 'control',
  prefix: 'control',
  sortBy: 'camera_name',
  sortOrder: 'asc',
  sortable: [
    'object_name', 'location_name', 'camera_name', 'control_status', 'potential_camera_hours',
    'technical_downtime_hours', 'manual_stopped_hours', 'available_control_hours',
    'continuity_percent', 'technical_continuity_percent',
  ],
  enumLevel: {
    key: 'statuses', param: 'statuses', label: 'Статус', all: 'Все статусы', options: CONTROL_STATUS_OPTIONS,
  },
});

/** comparison_*: строка = Объект + Локация (ТЗ §25). */
export const COMPARISON_TABLE = definition({
  id: 'comparison',
  prefix: 'comparison',
  sortBy: 'violations_per_100_control_hours',
  sortOrder: 'desc',
  sortable: [
    'object_name', 'location_name', 'violations_count', 'available_control_hours',
    'violations_per_100_control_hours', 'continuity_percent',
  ],
});

/** shift_*: строка = Объект + Локация + Камера + Смена (ТЗ §26). */
export const SHIFT_TABLE = definition({
  id: 'shift',
  prefix: 'shift',
  sortBy: 'violations_per_100_control_hours',
  sortOrder: 'desc',
  sortable: [
    'object_name', 'location_name', 'camera_name', 'shift_name', 'violations_count',
    'available_control_hours', 'violations_per_100_control_hours', 'continuity_percent',
  ],
  enumLevel: {
    key: 'shiftKeys', param: 'keys', label: 'Смена', all: 'Все смены', options: SHIFT_KEY_OPTIONS,
  },
});

export const ANALYTICS_TABLES = {
  control: CONTROL_TABLE,
  comparison: COMPARISON_TABLE,
  shift: SHIFT_TABLE,
};

/** Все фильтр-уровни таблицы: иерархия + enum (если у таблицы он есть). */
export const tableFilterLevels = (table) =>
  (table.enumLevel ? [...table.levels, table.enumLevel] : table.levels);

const fullParam = (table, param) => `${table.prefix}_${param}`;
const wireParam = (table, param) => `${table.wirePrefix}_${param}`;

const normalizeIds = (values) => {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const toPositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export const createTableState = (table) => ({
  ...Object.fromEntries(tableFilterLevels(table).map((level) => [level.key, []])),
  sortBy: table.defaults.sortBy,
  sortOrder: table.defaults.sortOrder,
  limit: table.defaults.limit,
  offset: table.defaults.offset,
});

export const normalizeTableState = (table, state) => {
  const sortBy = table.sortable.includes(state?.sortBy) ? state.sortBy : table.defaults.sortBy;
  const sortOrder = state?.sortOrder === 'asc' || state?.sortOrder === 'desc'
    ? state.sortOrder
    : table.defaults.sortOrder;
  const limit = TABLE_LIMIT_OPTIONS.includes(Number(state?.limit))
    ? Number(state.limit)
    : table.defaults.limit;
  return {
    ...Object.fromEntries(tableFilterLevels(table).map((level) => [level.key, normalizeIds(state?.[level.key])])),
    sortBy,
    sortOrder,
    limit,
    offset: toPositiveInt(state?.offset, 0),
  };
};

export const parseTableParams = (searchParams, table) => {
  const raw = { sortBy: null, sortOrder: null, limit: null, offset: null };
  if (searchParams) {
    raw.sortBy = searchParams.get(fullParam(table, 'sort_by'));
    raw.sortOrder = searchParams.get(fullParam(table, 'sort_order'));
    raw.limit = searchParams.get(fullParam(table, 'limit'));
    raw.offset = searchParams.get(fullParam(table, 'offset'));
  }
  const filters = Object.fromEntries(tableFilterLevels(table).map((level) => {
    const values = searchParams
      ? searchParams.getAll(fullParam(table, level.param)).flatMap((value) => String(value).split(','))
      : [];
    return [level.key, values];
  }));
  return normalizeTableState(table, { ...filters, ...raw });
};

/**
 * Значения по умолчанию в URL не пишем: адрес остаётся коротким, а Back/Forward
 * всё равно восстанавливает состояние — отсутствие параметра и есть default.
 */
export const applyTableParams = (searchParams, table, state) => {
  const next = new URLSearchParams(searchParams || '');
  const normalized = normalizeTableState(table, state);
  tableFilterLevels(table).forEach((level) => next.delete(fullParam(table, level.param)));
  ['sort_by', 'sort_order', 'limit', 'offset'].forEach((param) => next.delete(fullParam(table, param)));

  tableFilterLevels(table).forEach((level) => {
    normalized[level.key].forEach((id) => next.append(fullParam(table, level.param), id));
  });
  if (normalized.sortBy !== table.defaults.sortBy) next.set(fullParam(table, 'sort_by'), normalized.sortBy);
  if (normalized.sortOrder !== table.defaults.sortOrder) next.set(fullParam(table, 'sort_order'), normalized.sortOrder);
  if (normalized.limit !== table.defaults.limit) next.set(fullParam(table, 'limit'), String(normalized.limit));
  if (normalized.offset > 0) next.set(fullParam(table, 'offset'), String(normalized.offset));
  return next;
};

/**
 * state → параметры запроса. Сортировка и пагинация уходят всегда (backend
 * авторитетен и должен видеть ровно то, что показывает UI), пустые фильтры — нет.
 */
export const toTableQuery = (table, state) => {
  const normalized = normalizeTableState(table, state);
  const query = {
    [wireParam(table, 'sort_by')]: normalized.sortBy,
    [wireParam(table, 'sort_order')]: normalized.sortOrder,
    [wireParam(table, 'limit')]: normalized.limit,
  };
  if (normalized.offset > 0) query[wireParam(table, 'offset')] = normalized.offset;
  tableFilterLevels(table).forEach((level) => {
    const ids = normalized[level.key];
    if (ids.length === 0) return;
    query[wireParam(table, level.param)] = level.options
      ? ids
      : ids.map(Number).filter(Number.isFinite);
  });
  return query;
};

/** Текстовые колонки сортируются сначала по возрастанию, числовые — по убыванию. */
const NUMERIC_COLUMNS = new Set([
  'violations_count', 'violations_per_100_control_hours', 'available_control_hours', 'continuity_percent',
  'technical_continuity_percent', 'potential_camera_hours', 'technical_downtime_hours', 'manual_stopped_hours',
]);

export const defaultSortOrderFor = (column) => (NUMERIC_COLUMNS.has(column) ? 'desc' : 'asc');

export const setTableSort = (table, state, column) => {
  if (!table.sortable.includes(column)) return normalizeTableState(table, state);
  const current = normalizeTableState(table, state);
  const sortOrder = current.sortBy === column
    ? (current.sortOrder === 'asc' ? 'desc' : 'asc')
    : defaultSortOrderFor(column);
  return { ...current, sortBy: column, sortOrder, offset: 0 };
};

export const toggleTableFilter = (table, state, levelKey, id) => {
  const current = normalizeTableState(table, state);
  const value = String(id);
  const selected = current[levelKey] || [];
  const next = {
    ...current,
    [levelKey]: selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    offset: 0,
  };
  // Снятие родителя не оставляет висящих потомков: строка фильтра должна
  // совпадать с тем, что отправлено в запросе.
  const levelIndex = table.levels.findIndex((level) => level.key === levelKey);
  if (levelIndex >= 0 && next[levelKey].length === 0) {
    table.levels.slice(levelIndex + 1).forEach((level) => { next[level.key] = []; });
  }
  return next;
};

export const clearTableLevel = (table, state, levelKey) => {
  const current = normalizeTableState(table, state);
  const next = { ...current, [levelKey]: [], offset: 0 };
  const levelIndex = table.levels.findIndex((level) => level.key === levelKey);
  if (levelIndex >= 0) table.levels.slice(levelIndex + 1).forEach((level) => { next[level.key] = []; });
  return next;
};

export const clearTableFilters = (table, state) => {
  const current = normalizeTableState(table, state);
  return {
    ...current,
    ...Object.fromEntries(tableFilterLevels(table).map((level) => [level.key, []])),
    offset: 0,
  };
};

export const setTableOffset = (table, state, offset) => ({
  ...normalizeTableState(table, state),
  offset: Math.max(0, offset),
});

export const setTableLimit = (table, state, limit) => ({
  ...normalizeTableState(table, { ...state, limit }),
  offset: 0,
});

export const isTableFiltered = (table, state) =>
  tableFilterLevels(table).some((level) => normalizeIds(state?.[level.key]).length > 0);

export const countTableFilters = (table, state) =>
  tableFilterLevels(table).reduce((total, level) => total + normalizeIds(state?.[level.key]).length, 0);
